import { useCallback, useState } from 'react'

const STORAGE_KEY = 'servicedesk.tickets2.columnVisibility'
const PRIORITY_HIDDEN_MIGRATION_KEY = 'servicedesk.tickets2.migratePriorityHidden'

export type TicketsListColumnId =
  | 'status'
  | 'viewers'
  | 'priority'
  | 'weight'
  | 'project'
  | 'ticket_number'
  | 'subject'
  | 'created_at'
  | 'updated_at'
  | 'assigned'
  | 'customer'
  | 'channel'
  | 'messages'

/** Порядок колонок в таблице */
export const TICKETS_LIST_COLUMN_ORDER: TicketsListColumnId[] = [
  'status',
  'viewers',
  'priority',
  'weight',
  'project',
  'ticket_number',
  'subject',
  'created_at',
  'updated_at',
  'assigned',
  'customer',
  'channel',
  'messages',
]

/** Веса для пропорций ширины (как прежние проценты до нормализации) */
export const TICKETS_LIST_COLUMN_WEIGHT: Record<TicketsListColumnId, number> = {
  status: 7.5,
  viewers: 3.5,
  priority: 3.5,
  weight: 3,
  project: 3.5,
  ticket_number: 4.5,
  subject: 25,
  created_at: 11,
  updated_at: 12,
  assigned: 9,
  customer: 9.5,
  channel: 6.5,
  messages: 4.5,
}

export const TICKETS_LIST_COLUMN_LABEL: Record<TicketsListColumnId, string> = {
  status: 'Статус',
  viewers: 'Просматривают',
  priority: 'Приоритет',
  weight: 'Вес',
  project: 'Проект',
  ticket_number: '#',
  subject: 'Тема',
  created_at: 'Создан',
  updated_at: 'Обновлён',
  assigned: 'Назначен',
  customer: 'Пользователь',
  channel: 'Канал',
  messages: 'Сообщений',
}

/** Колонки в меню «Колонки» (тема всегда видима — как в старом фронте) */
export const TICKETS_LIST_COLUMNS_MENU_ORDER: TicketsListColumnId[] =
  TICKETS_LIST_COLUMN_ORDER.filter((id) => id !== 'subject')

const DEFAULT_VISIBILITY: Record<TicketsListColumnId, boolean> = {
  status: true,
  viewers: true,
  priority: false,
  weight: true,
  project: true,
  ticket_number: true,
  subject: true,
  created_at: true,
  updated_at: true,
  assigned: true,
  customer: true,
  channel: false,
  messages: true,
}

export type TicketsListColumnVisibility = Record<TicketsListColumnId, boolean>

export function isTicketsListColumnVisible(
  id: TicketsListColumnId,
  visibility: TicketsListColumnVisibility,
): boolean {
  if (id === 'subject') return true
  return visibility[id] !== false
}

function loadFromStorage(): Partial<TicketsListColumnVisibility> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    return parsed as Partial<TicketsListColumnVisibility>
  } catch {
    return null
  }
}

function mergeVisibility(partial: Partial<TicketsListColumnVisibility> | null): TicketsListColumnVisibility {
  return { ...DEFAULT_VISIBILITY, ...partial }
}

export function loadTicketsListColumnVisibility(): TicketsListColumnVisibility {
  const merged = mergeVisibility(loadFromStorage())
  try {
    if (localStorage.getItem(PRIORITY_HIDDEN_MIGRATION_KEY) !== '1') {
      merged.priority = false
      saveTicketsListColumnVisibility(merged)
      localStorage.setItem(PRIORITY_HIDDEN_MIGRATION_KEY, '1')
    }
  } catch {
    /* ignore */
  }
  return merged
}

export function saveTicketsListColumnVisibility(v: TicketsListColumnVisibility): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v))
  } catch {
    /* ignore */
  }
}

/** Проценты ширины только для видимых колонок (сумма 100). */
export function ticketsListColumnWidthsPercent(
  visibility: TicketsListColumnVisibility,
): { id: TicketsListColumnId; widthPct: string }[] {
  const visible = TICKETS_LIST_COLUMN_ORDER.filter((id) => isTicketsListColumnVisible(id, visibility))
  const sum = visible.reduce((s, id) => s + TICKETS_LIST_COLUMN_WEIGHT[id], 0)
  if (sum <= 0) return visible.map((id) => ({ id, widthPct: `${100 / visible.length}%` }))
  return visible.map((id) => ({
    id,
    widthPct: `${(TICKETS_LIST_COLUMN_WEIGHT[id] / sum) * 100}%`,
  }))
}

export function useTicketsListColumnVisibility() {
  const [visibility, setVisibility] = useState<TicketsListColumnVisibility>(loadTicketsListColumnVisibility)

  const setColumnVisible = useCallback((id: TicketsListColumnId, visible: boolean) => {
    if (id === 'subject') return
    setVisibility((prev) => {
      const next = { ...prev, [id]: visible }
      saveTicketsListColumnVisibility(next)
      return next
    })
  }, [])

  return { visibility, setColumnVisible }
}
